/**
 * App-open heartbeat — the retention signal the server has been waiting for.
 *
 * WHY THIS EXISTS
 *   Nothing recorded that a trainer opened the app. The admin dashboard showed
 *   a `sessions` count, which reads 1 for almost everyone because a session row
 *   is one SIGN-IN on a 30-day token (deleted on sign-out) — reopening the app
 *   reuses the JWT and creates nothing. A daily user and someone who opened the
 *   app once and never came back were the same number.
 *
 *   The fallback signal, distinct days with a dex write, is honest but partial:
 *   it only sees days the trainer CHANGED something. Browsing a dex, checking a
 *   trade or reading the event feed are real returns that write nothing, and
 *   they are exactly what a retention question is about.
 *
 *   POST /api/friends/presence/heartbeat has existed since J11 (v1.3.5) and
 *   sets accounts.last_seen_at. No client build ever called it, which is why
 *   that column is null for nearly every account. This is the caller.
 *
 * WHAT IT DOES NOT DO
 *   It does not track screens, taps, routes or anything about WHAT the trainer
 *   did — the server records one row per account per UTC day. It is a "was the
 *   app open today" bit, deliberately, because that is the whole question and
 *   anything more would be surveillance with no owner asking for it.
 *
 * FAILURE IS SILENT BY DESIGN
 *   A heartbeat must never surface an error, block a render, or retry hard. A
 *   missed beat costs one day's data point; a heartbeat that interrupts the app
 *   costs the user something real.
 */

import { getDeviceReport, type DeviceReport } from "./deviceReport";

/** Last time a beat actually went out (ms epoch), per device. */
export const HEARTBEAT_KEY = "nexus.appHeartbeat.v1";

/**
 * Minimum gap between beats. Fifteen minutes is short enough that a real
 * session is always recorded and long enough that a user tabbing in and out
 * does not put a request behind every visibility flip. The server counts
 * `opens` per day, so this also decides what "an open" means: a foreground
 * visit, not a tap.
 */
export const HEARTBEAT_MIN_GAP_MS = 15 * 60 * 1000;

function lastBeatAt(): number {
  if (typeof localStorage === "undefined") return 0;
  try {
    const n = Number.parseInt(localStorage.getItem(HEARTBEAT_KEY) ?? "", 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** Throttle gate. Exported for tests; callers go through sendAppHeartbeat. */
export function shouldSendHeartbeat(now: number = Date.now()): boolean {
  return now - lastBeatAt() >= HEARTBEAT_MIN_GAP_MS;
}

/**
 * Send a beat if the throttle allows it. Resolves `{ sent }` — never rejects.
 *
 * The timestamp is stamped only on a SUCCESSFUL beat, so an offline launch
 * retries on the next foreground rather than being thrown away for 15 minutes.
 */
export async function sendAppHeartbeat(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
  isAuthed: () => boolean,
  now: number = Date.now(),
  getDevice: () => Promise<DeviceReport | null> = getDeviceReport,
): Promise<{ sent: boolean }> {
  // Checked per beat, not once at boot: someone who signs in mid-session should
  // start being counted on their next foreground, not on their next launch.
  if (!isAuthed()) return { sent: false };
  if (!shouldSendHeartbeat(now)) return { sent: false };
  try {
    // The actual device + app build (lib/deviceReport), native builds only.
    // Servers before migration 0095 ignore the body; a failed read sends none.
    const device = await getDevice().catch(() => null);
    const res = device
      ? await apiFetch("POST", "/api/friends/presence/heartbeat", { device })
      : await apiFetch("POST", "/api/friends/presence/heartbeat");
    if (!res.ok) return { sent: false };
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(HEARTBEAT_KEY, String(now));
      } catch {
        /* quota — worst case we beat again next foreground */
      }
    }
    return { sent: true };
  } catch {
    // Offline, signed out, or the endpoint is not there yet. Not stamping the
    // marker is the retry.
    return { sent: false };
  }
}

/**
 * Beat now, and on every return to the foreground.
 *
 * Returns a teardown function. `visibilitychange` is the event that matters on
 * mobile: the app is not reloaded when it comes back from the background, so a
 * mount-only beat would record the day someone installed the app and never
 * another one.
 */
export function startAppHeartbeat(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
  isAuthed: () => boolean,
): () => void {
  const beat = () => {
    void sendAppHeartbeat(apiFetch, isAuthed).catch(() => {
      /* never surfaces */
    });
  };

  beat();

  if (typeof document === "undefined") return () => {};
  const onVisible = () => {
    if (document.visibilityState === "visible") beat();
  };
  document.addEventListener("visibilitychange", onVisible);
  return () => document.removeEventListener("visibilitychange", onVisible);
}

/** Test seam. */
export function _resetAppHeartbeatForTests(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(HEARTBEAT_KEY);
  } catch {
    /* ignore */
  }
}
